import React, { useState, useRef, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { useMediaUpload, getMediaUrl } from '../../../hooks/useMediaUpload';
import resolveAvatar from '../../../lib/resolveAvatar';

interface PostCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
  }) => Promise<void>;
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  } | null;
  theme: {
    textColor: string;
    accentColor: string;
    cardBg: string;
  };
  scale: { w: number; h: number };
  scaleW: (val: number, scale: number) => number;
  scaleH: (val: number, scale: number) => number;
  getCardBackground: (theme: any, customization: any, fallback: string) => string;
  customization?: any;
}

export function PostCreationModal({
  isOpen,
  onClose,
  onSubmit,
  currentUser,
  theme,
  scale,
  scaleW,
  scaleH,
  getCardBackground,
  customization
}: PostCreationModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPosition, setMediaPosition] = useState<'above' | 'below'>('below');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, uploadProgress, fileId, error: uploadError } = useMediaUpload();

  // Update media URL when file upload completes
  useEffect(() => {
    if (fileId) {
      setMediaUrl(getMediaUrl(fileId));
    }
  }, [fileId]);

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const tags = tagInput
        .split(/[,\s#]+/)
        .map(t => t.trim())
        .filter(t => t.length > 0);

      await onSubmit({
        title,
        content,
        tags,
        mediaUrl,
        mediaPosition
      });

      // Reset form
      setTitle('');
      setContent('');
      setTagInput('');
      setMediaUrl('');
      setMediaPosition('below');
      onClose();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadFile(file);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload file. Please try again.');
      }
    }
    if (e.target) e.target.value = '';
  };

  // Detect mobile mode via scale (mobile sets scale to {w:1, h:1})
  const isMobileModal = scale.w === 1 && scale.h === 1;

  if (!isOpen) return null;

  return (
    <>
    {/* Mobile backdrop */}
    {isMobileModal && (
      <div
        className="fixed inset-0"
        style={{ zIndex: 99, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        onTouchStart={onClose}
      />
    )}
    <div
      className="fixed rounded-xl shadow-2xl border"
      style={isMobileModal ? {
        // Mobile: centered bottom-sheet style
        bottom: 0,
        left: 0,
        right: 0,
        top: 'auto',
        backgroundColor: theme.cardBg,
        borderColor: theme.accentColor + '60',
        zIndex: 100,
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        animation: 'slideUp 0.3s ease-out',
      } : {
        // Desktop: original positioning
        top: '10rem',
        left: '1.5rem',
        backgroundColor: theme.cardBg,
        borderColor: theme.accentColor + '60',
        zIndex: 43,
        width: '22vw',
        maxWidth: '600px',
        minWidth: '350px',
        animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
        <div style={{ padding: `${scaleH(20, scale.h)}px` }}>
          {/* Header */}
          <div className="flex items-center justify-between" style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
            <h3 className="font-bold" style={{ color: theme.textColor, fontSize: `${scaleW(18, scale.w)}px` }}>
              Create Post
            </h3>
            <button
              onClick={onClose}
              className="hover:opacity-70 transition-opacity"
              style={{ color: theme.textColor }}
            >
              <X size={20} />
            </button>
          </div>

          {/* User Info */}
          {currentUser && (
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${theme.accentColor}, ${theme.accentColor}dd)` }}
              >
                {currentUser.avatar ? (
                  <img src={resolveAvatar(currentUser.avatar) || currentUser.avatar} alt="You" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span style={{ color: theme.textColor }}>👤</span>
                )}
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: theme.textColor }}>
                  {currentUser.displayName || currentUser.username}
                </p>
                <p className="text-xs opacity-70" style={{ color: theme.textColor }}>
                  @{currentUser.username}
                </p>
              </div>
            </div>
          )}

          {/* Title Input */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)..."
            className="w-full px-4 py-3 rounded-lg text-base font-bold mb-3 border focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderColor: `${theme.accentColor}40`,
              color: theme.textColor
            }}
            maxLength={100}
          />

          {/* Content Textarea */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your Earth-positive actions..."
            className="w-full rounded-lg p-3 text-sm resize-none border focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderColor: `${theme.accentColor}40`,
              color: theme.textColor
            }}
            rows={isMobileModal ? 5 : 10}
            maxLength={5000}
          />
          <div className="text-right text-xs mt-1 opacity-70" style={{ color: theme.textColor }}>
            {content.length}/5000
          </div>

          {/* Media Section */}
          <div className="mb-3">
            <input
              type="text"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="Image URL (optional)"
              className="w-full p-3 rounded-lg mb-2 focus:outline-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all disabled:opacity-50"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`
                }}
              >
                <Upload size={16} />
                {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Media'}
              </button>
              {mediaUrl && (
                <button
                  onClick={() => setMediaUrl('')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                    color: theme.textColor,
                    border: '1px solid rgba(255, 0, 0, 0.4)'
                  }}
                >
                  <X size={16} />
                  Clear
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Media Position Selector */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setMediaPosition('above')}
                className="flex-1 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: mediaPosition === 'above' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'above' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'above' ? theme.accentColor : theme.accentColor + '30'}`
                }}
              >
                Above Content
              </button>
              <button
                onClick={() => setMediaPosition('below')}
                className="flex-1 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: mediaPosition === 'below' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'below' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'below' ? theme.accentColor : theme.accentColor + '30'}`
                }}
              >
                Below Content
              </button>
            </div>
          </div>

          {/* Tags Input */}
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Tags (e.g. #tag1 #tag2 or tag1, tag2)"
            className="w-full p-3 rounded-lg mb-4 focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.textColor,
              border: `1px solid ${theme.accentColor}40`
            }}
          />

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
            style={{
              backgroundColor: theme.accentColor,
              color: theme.textColor
            }}
          >
            {isSubmitting ? 'Posting...' : 'Post'}
          </button>
        </div>
    </div>
    </>
  );
}
